import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/Login";
import SetPasswordPage from "@/pages/SetPasswordPage";
import {
  PortalLogin,
  PortalActivate,
  PortalResetRequest,
  PortalReset,
} from "@/pages/portal/PortalAuthPages";
import W3bPortal from "@/pages/portal/W3bPortal";
import Dashboard from "@/pages/Dashboard";
import EmptyState from "@/pages/EmptyState";

import CustomersList from "@/pages/customers/CustomersList";
import CustomerDetail from "@/pages/customers/CustomerDetail";
import ContactsList from "@/pages/contacts/ContactsList";
// RC4 · OrganizationsList no longer routed; /organizations redirects
// to /clients. Import kept commented for context — remove after RC4
// once no other module references it.
// import OrganizationsList from "@/pages/organizations/OrganizationsList";
import FleetList from "@/pages/fleet/FleetList";
import FleetDetail from "@/pages/fleet/FleetDetail";
import FleetMapWorkspace from "@/pages/fleet/FleetMapWorkspace";
import BillingPage from "@/pages/billing/BillingPage";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import QuotesContractsPage from "@/pages/quotes/QuotesContractsPage";
import AdminGuard from "@/components/AdminGuard";
import DriversList from "@/pages/drivers/DriversList";
import DriverDetail from "@/pages/drivers/DriverDetail";
import MyDriverProfile from "@/pages/drivers/MyDriverProfile";

import ToursList from "@/pages/tours/ToursList";
import TourDetail from "@/pages/tours/TourDetail";
import SchedulePage from "@/pages/schedule/SchedulePage";
import DispatchPage from "@/pages/dispatch/DispatchPage";

import ServicePage from "@/pages/service/ServicePage";
import ClientPortal from "@/pages/portal/ClientPortal";
import IntegrationsPage from "@/pages/administration/IntegrationsPage";
import MotiveCatalogPage from "@/pages/administration/MotiveCatalogPage";
import SystemSettingsPage from "@/pages/administration/SystemSettingsPage";
import AutoArchiveSettings from "@/pages/administration/AutoArchiveSettings";
// F7CPurgePage removed at Final Handoff Gate (client-delivery-rc1).
import AuditLogPage from "@/pages/administration/AuditLogPage";
import AdminUsersPage, { AdminUserDetailPage } from "@/pages/administration/AdminUsersPage";
import AdminRolesPage, { AdminRoleDetailPage, AdminUserNewPage } from "@/pages/administration/AdminRolesPage";
import LegacyImportPage from "@/pages/administration/LegacyImport";
import AdminQuoteRates from "@/pages/admin/AdminQuoteRates";
import UnitTypesPage from "@/pages/admin/UnitTypesPage";
import AssetOwnersPage from "@/pages/fleet/AssetOwnersPage";
import ReportsIndex, {
  ReportFleetUtilization,
  ReportActiveTours,
  ReportUpcoming,
  ReportCoachAvailability,
  ReportQuotePipeline,
} from "@/pages/reports/ReportsIndex";
import {
  ReportClientContact,
  ReportDriverAvailability,
  ReportDepartures,
  ReportEndOfTour,
  ReportLongTermClient,
  ReportFleetUtilization as ReportFleetUtilizationV2,
  ReportFuelEstimate,
  ReportBusAvailability,
  ReportDriverTourDays,
  ReportDriverUtilization,
} from "@/pages/reports/LegacyReports";

const Empty = (title, note) => <EmptyState title={title} note={note} />;

// RC4 · Preserve /organizations/:id deep links after Clients consolidation.
function OrganizationsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/clients/${id}`} replace />;
}


function DashboardOrDriverProfile() {
  const { user } = useAuth();
  // Only redirect to the driver profile if the user is a legacy driver
  // AND has no assigned role class. Shop/technician accounts also carry
  // `legacy_role="driver"` (lowest legacy privilege) but have a role
  // class assigned — those users should reach the Dashboard so the
  // `FutureShopShell` renders for them.
  if (user?.role === "driver" && !user?.role_class_id) {
    return <Navigate to="/my-driver" replace />;
  }
  return <Dashboard />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<ProtectedRoute><SetPasswordPage /></ProtectedRoute>} />
          {/* CC3.MVP W3a · Portal auth pages (aa_portal_token only). Must sit ABOVE the /portal/* wildcard. */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/activate/:token" element={<PortalActivate />} />
          <Route path="/portal/reset-request" element={<PortalResetRequest />} />
          <Route path="/portal/reset/:token" element={<PortalReset />} />
          {/* CC3.MVP W3b · Portal shell + client-scoped reads. Owns the entire /portal/clients/* subtree. Must sit ABOVE the /portal/* wildcard. */}
          <Route path="/portal/clients/*" element={<W3bPortal />} />

          {/* Legacy (W2) portal (uses its own scoped token) */}
          <Route path="/portal/*" element={<ClientPortal />} />

          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardOrDriverProfile />} />
            <Route path="/my-driver" element={<MyDriverProfile />} />

            <Route path="/customers" element={<CustomersList />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/contacts" element={<ContactsList />} />
            <Route path="/contacts/:contactId" element={<ContactsList />} />
            {/* RC4 · /clients + /organizations redirect + alias to the
                canonical /customers route. Landing screen adopts the
                Organizations-style directory presentation via
                CustomersList (which already renders the org-style
                directory columns). Underlying APIs + DB collections
                unchanged. */}
            <Route path="/clients" element={<CustomersList />} />
            <Route path="/clients/:id" element={<CustomerDetail />} />
            <Route path="/organizations" element={<Navigate to="/clients" replace />} />
            <Route path="/organizations/:id" element={<OrganizationsRedirect />} />
            <Route path="/fleet" element={<FleetList />} />
            <Route path="/fleet/map" element={<FleetMapWorkspace />} />
            <Route path="/fleet/:id" element={<FleetDetail />} />
            <Route path="/drivers" element={<DriversList />} />
            <Route path="/drivers/:id" element={<DriverDetail />} />

            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/tours" element={<ToursList />} />
            <Route path="/tours/:id" element={<TourDetail />} />
            {/* Correction Round 1 (2026-02) · Deterministic deep-link for the
                Pricing tab. Previously `/tours/{id}/pricing` fell through to
                the wildcard route and redirected to /dashboard, which was
                observed by the tester as an infinite "Checking session…"
                loop when navigating post-login. Now this path routes to
                TourDetail with `initialTab=pricing` derived from the URL. */}
            <Route path="/tours/:id/pricing" element={<TourDetail />} />
            <Route path="/dispatch" element={<DispatchPage />} />
            <Route path="/quotes" element={<QuotesContractsPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/maintenance" element={<ServicePage />} />
            <Route path="/service" element={<ServicePage />} />
            <Route path="/documents" element={<DocumentsPage />} />

            <Route path="/reports" element={<ReportsIndex />} />
            <Route path="/reports/fleet-utilization" element={<ReportFleetUtilizationV2 />} />
            <Route path="/reports/active-tours" element={<ReportActiveTours />} />
            <Route path="/reports/upcoming-departures" element={<ReportUpcoming />} />
            <Route path="/reports/coach-availability" element={<ReportCoachAvailability />} />
            <Route path="/reports/quote-pipeline" element={<ReportQuotePipeline />} />
            <Route path="/reports/client-contact" element={<ReportClientContact />} />
            <Route path="/reports/driver-availability" element={<ReportDriverAvailability />} />
            <Route path="/reports/departures" element={<ReportDepartures />} />
            <Route path="/reports/end-of-tour" element={<ReportEndOfTour />} />
            <Route path="/reports/long-term-client" element={<ReportLongTermClient />} />
            <Route path="/reports/fuel-estimate" element={<ReportFuelEstimate />} />
            <Route path="/reports/bus-availability" element={<ReportBusAvailability />} />
            <Route path="/reports/driver-tour-days" element={<ReportDriverTourDays />} />
            <Route path="/reports/driver-utilization" element={<ReportDriverUtilization />} />

            <Route path="/administration" element={<AdminGuard><Navigate to="/administration/users" replace /></AdminGuard>} />
            <Route path="/administration/users" element={<AdminGuard><AdminUsersPage /></AdminGuard>} />
            <Route path="/administration/users/new" element={<AdminGuard><AdminUserNewPage /></AdminGuard>} />
            <Route path="/administration/users/:id" element={<AdminGuard><AdminUserDetailPage /></AdminGuard>} />
            <Route path="/administration/roles" element={<AdminGuard><AdminRolesPage /></AdminGuard>} />
            <Route path="/administration/roles/:key" element={<AdminGuard><AdminRoleDetailPage /></AdminGuard>} />
            {/* Permissions + Dashboard Defaults are managed per-role-class inside
                the Roles editor. These aliases keep deep-links working. */}
            <Route path="/administration/permissions" element={<AdminGuard><Navigate to="/administration/roles" replace /></AdminGuard>} />
            <Route path="/administration/dashboard-defaults" element={<AdminGuard><Navigate to="/administration/roles" replace /></AdminGuard>} />
            <Route path="/administration/dashboards" element={<AdminGuard><Navigate to="/administration/roles" replace /></AdminGuard>} />
            <Route path="/administration/integrations" element={<AdminGuard><IntegrationsPage /></AdminGuard>} />
            <Route path="/administration/integrations/motive" element={<AdminGuard><MotiveCatalogPage /></AdminGuard>} />
            <Route path="/administration/imports/legacy" element={<AdminGuard><LegacyImportPage /></AdminGuard>} />
            <Route path="/administration/settings" element={<AdminGuard><SystemSettingsPage /></AdminGuard>} />
            <Route path="/administration/quote-rates" element={<AdminGuard><AdminQuoteRates /></AdminGuard>} />
            <Route path="/administration/fleet-settings/unit-types" element={<AdminGuard><UnitTypesPage /></AdminGuard>} />
            <Route path="/fleet/asset-owners" element={<AssetOwnersPage />} />
            <Route path="/administration/system-config" element={<AdminGuard><SystemSettingsPage /></AdminGuard>} />
            <Route path="/administration/system-settings" element={<AdminGuard><SystemSettingsPage /></AdminGuard>} />
            <Route path="/administration/auto-archive" element={<AdminGuard><AutoArchiveSettings /></AdminGuard>} />
            {/* Final Handoff Gate cleanup — F7C Purge route
                removed with the danger surface. Historical audit
                rows preserved. */}
            {/* Priority-0 (ops-ux-documents-2026-09-23) — Audit Log
                is Super-Admin-only. Intentionally NOT wrapped in
                <AdminGuard>: standard Admins and non-admins alike
                must land on the AuditLogPage which then renders its
                Not-Authorized (403) surface with data-testid
                `audit-log-not-authorized`. AdminGuard would have
                silently redirected non-admins to /dashboard which
                violates the P0 rule "returns 403 page (not silent
                hide)". Anonymous is still rejected by the outer
                <ProtectedRoute> that wraps the workspace shell. */}
            <Route path="/administration/audit-log" element={<AuditLogPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
